import { EpocusRole } from "elephant-web/gql";
import { useMemo } from "react";
import { useEpocusProgramUsersQuery } from "../settings/users/_lib/__generated__/program-users-operations.gql";

export type DirectoryMember = {
  id: string;
  name: string;
  role: EpocusRole;
};

const REVIEWING_ROLES = new Set<EpocusRole>([
  EpocusRole.Reviewer,
  EpocusRole.PerformerReviewer,
  EpocusRole.QaAdmin,
  EpocusRole.ProgramAdmin,
]);

const memberName = (
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  email: string | null | undefined,
  id: string,
): string =>
  [firstName, lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ") ||
  email?.trim() ||
  id;

/**
 * The program's own members, for pickers that must write a principal id.
 * Sourced from the membership list rather than from whoever happens to appear
 * in a stats rollup: a reviewer with no open work and no recent signature is
 * absent from those, and would silently be unpickable.
 *
 * `id` is the platform user id, not the sibling `epocusUserId` on the same
 * membership row — `assignedReviewerId` and `performerId` are matched against
 * the former, so the latter would assign work to a principal no queue holds.
 */
export const useProgramDirectory = (
  programId: string | null,
): { members: DirectoryMember[]; reviewers: DirectoryMember[] } => {
  const { data } = useEpocusProgramUsersQuery({
    variables: { programId: programId ?? "" },
    skip: !programId,
    fetchPolicy: "cache-and-network",
  });

  return useMemo(() => {
    const members = (data?.listProgramUsers ?? []).flatMap(
      ({ role, user }): DirectoryMember[] =>
        user
          ? [
              {
                id: user.id,
                name: memberName(
                  user.firstName,
                  user.lastName,
                  user.email,
                  user.id,
                ),
                role,
              },
            ]
          : [],
    );

    return {
      members,
      reviewers: members.filter(({ role }) => REVIEWING_ROLES.has(role)),
    };
  }, [data]);
};
